import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Row,
  Col,
  Tag,
  message,
  Card,
  Descriptions,
  Space,
  Typography,
  Empty,
  Table
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { StoreDisplayGroup, BoundShelf } from '../../../types/storeDisplayGroup';
import StoreDisplayGroupService from '../../../services/storeDisplayGroupService';
// import { DetailSkeleton } from '../../../components/common/LoadingSkeleton';
import NetworkError from '../../../components/common/NetworkError';
import { handleApiResponse } from '../../../utils/responseTransformer';
import { isRetryableError, sanitizeErrorForDisplay } from '../../../utils/errorHandler';

import dayjs from 'dayjs';

const { Title } = Typography;

const StoreDisplayGroupDetail: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State management
  const [data, setData] = useState<StoreDisplayGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load display group detail
  const loadData = useCallback(async (showLoading = true) => {
    if (!code) return;

    if (showLoading) setLoading(true);
    setError(null);

    try {
      const response = await StoreDisplayGroupService.getStoreDisplayGroupByCode(code);
      const displayGroup = handleApiResponse(response, 'loadStoreDisplayGroupDetail');
      setData(displayGroup as StoreDisplayGroup);
    } catch (error) {
      const errorMessage = sanitizeErrorForDisplay(error);
      setError(errorMessage);
      setData(null);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [code]);

  // Initial data load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate('/store-display-groups');
  }, [navigate]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    try {
      await loadData();
      message.success(t('storeDisplayGroup.refreshSuccess'));
    } catch (error) {
      message.error(t('storeDisplayGroup.refreshFailed'));
    }
  }, [loadData, t]);

  // Handle retry for network errors
  const handleRetry = useCallback(() => {
    loadData();
  }, [loadData]);

  // Show loading skeleton
  // if (loading) {
  //   return (
  //     <div className="page-container">
  //       <DetailSkeleton />
  //     </div>
  //   );
  // }

  // Show error state
  if (error && isRetryableError({ message: error })) {
    return (
      <div className="page-container">
        <NetworkError onRetry={handleRetry} message={error} />
      </div>
    );
  }

  // Show not found error
  if (error && !data) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">{t('storeDisplayGroup.detailTitle')}</h1>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t('storeDisplayGroup.back')}
          </Button>
        </div>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h3>{t('storeDisplayGroup.notExist')}</h3>
          <p>{t('storeDisplayGroup.notExistDesc', { code })}</p>
          <Button type="primary" onClick={handleBack}>
            {t('storeDisplayGroup.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Shelf binding table columns
  const shelfColumns: ColumnsType<BoundShelf> = [
    {
      title: t('storeDisplayGroup.shelfBinding.shelfCode'),
      dataIndex: 'shelfCode',
      key: 'shelfCode',
    },
    {
      title: t('sectionType.typeName'),
      dataIndex: 'sectionTypeName',
      key: 'sectionTypeName',
    },
    {
      title: t('storeDisplayGroup.shelfBinding.layerCount'),
      dataIndex: 'shelfLayerCount',
      key: 'shelfLayerCount',
      align: 'center',
      render: (shelfLayerCount: number) => (
        <span style={{ fontWeight: 500 }}>{shelfLayerCount}</span>
      ),
    },

  ];

   // Format datetime display
  const formatDateTime = (timestamp: number): string => {
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            >
              {t('storeDisplayGroup.back')}
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {t('storeDisplayGroup.detailTitle')}
            </Title>
          </Space>
        </Col>
      </Row>

      {/* Basic Information */}
      <Card title={t('storeDisplayGroup.basicInfo.title')} style={{ marginBottom: '16px' }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t('storeDisplayGroup.basicInfo.code')}>
            {data.code}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeDisplayGroup.basicInfo.displayGroupCode')}>
            {data.displayGroupCode}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeDisplayGroup.basicInfo.name')}>
            {data.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeDisplayGroup.basicInfo.shop')}>
            {data.storeCode} - {data.storeName}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeDisplayGroup.basicInfo.shopGroup')}>
            {data.shopGroupCode} - {data.shopGroupName}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeDisplayGroup.basicInfo.createTime')}>
            {formatDateTime(data.gmtCreate)}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeDisplayGroup.basicInfo.updateTime')}>
            {formatDateTime(data.gmtModified)}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeDisplayGroup.basicInfo.dataStatus')}>
            <Tag color="blue">{t('storeDisplayGroup.basicInfo.readonly')}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('storeDisplayGroup.basicInfo.shelfSource')}>
            {t('storeDisplayGroup.basicInfo.digitalSpace')}
          </Descriptions.Item>
          {data.remark && (
            <Descriptions.Item label={t('storeDisplayGroup.basicInfo.remark')} span={2}>
              {data.remark}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Category Configuration */}
      <Card title={t('storeDisplayGroup.categoryConfig.title')} style={{ marginBottom: '16px' }}>
        {data.subClassSets.length === 0 ? (
          <Empty description={t('storeDisplayGroup.categoryConfig.noData')} />
        ) : (
          <div>
            <div style={{ marginBottom: '12px' }}>
              <span style={{ fontWeight: 'bold' }}>
                {t('storeDisplayGroup.categoryConfig.totalCategories', { count: data.subClassSets.length })}
              </span>
            </div>

            {/* Group by division */}
            {Object.entries(
              data.subClassSets.reduce((acc, sc) => {
                if (!acc[sc.divisionName]) {
                  acc[sc.divisionName] = [];
                }
                acc[sc.divisionName].push(sc);
                return acc;
              }, {} as Record<string, typeof data.subClassSets>)
            ).map(([division, subClasses]) => (
              <div key={division} style={{ marginBottom: '16px' }}>
                <div style={{
                  fontWeight: 'bold',
                  marginBottom: '8px',
                  color: '#1890ff'
                }}>
                  {division}:
                </div>
                <Space wrap>
                  {subClasses.map(sc => (
                    <Tag key={sc.categoryId} style={{ marginBottom: '4px' }}>
                      {sc.divisionName} {'>'} {sc.departmentName} {'>'} {sc.className} {'>'} {sc.subClassName}
                    </Tag>
                  ))}
                </Space>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Shelf Binding Information */}
      <Card title={t('storeDisplayGroup.shelfBinding.title')} style={{ marginBottom: '16px' }}>
        {data.shelfSet.length === 0 ? (
          <Empty description={t('storeDisplayGroup.shelfBinding.noData')} />
        ) : (
          <Table
            columns={shelfColumns}
            dataSource={data.shelfSet}
            rowKey="shelfCode"
            pagination={false}
            size="small"
          />
        )}

        <div style={{
          marginTop: 12,
          padding: '8px 12px',
          background: '#f5f5f5',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#666'
        }}>
          <strong>{t('common.description')}：</strong>{t('storeDisplayGroup.shelfBinding.description')}
        </div>
      </Card>   

      {/* SKU Quota Information */}
      <Card title={t('storeDisplayGroup.skuQuota.title')} style={{ marginBottom: '16px' }}>
        <Row gutter={[32, 16]}>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                {data.itemMaxQuota ? data.itemMaxQuota : '0'}
              </div>
              <div style={{ color: '#666' }}>{t('storeDisplayGroup.skuQuota.maxSkuCount')}</div>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                {data.itemMinQuota ? data.itemMinQuota : '0'}
              </div>
              <div style={{ color: '#666' }}>{t('storeDisplayGroup.skuQuota.minSkuCount')}</div>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                {data.itemMaxQuota - data.itemMinQuota}
              </div>
              <div style={{ color: '#666' }}>{t('storeDisplayGroup.skuQuota.skuCountFlexibility')}</div>
            </div>
          </Col>
        </Row>

        <div style={{
          marginTop: 16,
          padding: '8px 12px',
          background: '#f0f9ff',
          borderRadius: '4px',
          fontSize: '12px',
          color: '#666'
        }}>
          <strong>{t('common.description')}：</strong>{t('storeDisplayGroup.skuQuota.description')}
        </div>
      </Card>
    </div>
  );
};

export default StoreDisplayGroupDetail;